/**
 * The kebab at the end of the ticket head, and the one entry behind it.
 *
 * Delete used to be a worded red pill out on the strip, and beside a ticket
 * subject the three controls did not fit on one line: the strip wrapped, and
 * what dropped onto the second row was Delete, alone, in red, under a head that
 * had nothing else down there. The project head had the same fault and was
 * fixed the same way (`ProjectHead.tsx`) - the actions an operator came to the
 * page to perform stay out where they can be seen, and the one that ends the
 * ticket goes behind a menu it has to be looked for in.
 *
 * One entry, deliberately. This head has exactly one action that is not worth a
 * pill, so the menu holds exactly that; the point of the kebab here is not to
 * gather a list but to keep the destructive action off the primary strip.
 *
 * Its own component rather than JSX inside `TicketDetail`, because
 * `useClosePops` reads the host that `TicketDetail` itself renders - a hook call
 * up there would sit outside its own provider. The close matters: a `Pop`
 * unmounts its children the moment it shuts, so the menu has to close BEFORE the
 * confirm dialog opens, and that dialog stays with `TicketDetail`, outside the
 * popover, for the same unmounting reason.
 */
import { MoreVertical, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Btn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";

/** Inside the popover, which is the only place `useClosePops` has a host. */
function DeleteItem({ onDelete }: { onDelete: () => void }) {
  const t = useT();
  const close = useClosePops();

  return (
    <button
      type="button"
      className="crm-mi crm-mi-danger"
      onClick={() => {
        close();
        onDelete();
      }}
    >
      <Trash2 className="size-4" />
      {t("common.delete")}
    </button>
  );
}

export function TicketHeadMenu({ onDelete }: { onDelete: () => void }) {
  const t = useT();

  return (
    <Pop
      trigger={(open) => (
        <Btn
          round
          className={open ? "is-on" : undefined}
          title={t("common.more")}
          aria-label={t("common.more")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreVertical className="size-4" />
        </Btn>
      )}
    >
      <DeleteItem onDelete={onDelete} />
    </Pop>
  );
}
